// SeeAllAuditPage — every read that crossed customer boundaries, newest first.
//
// The portal's face on operations/templates/operations/audit_log.html, which it
// will replace. Four columns, the template's: when, who, what, why. Rows are
// written before the data they describe is returned and are never updated, so
// this is a record of what was asked for rather than of what came back.
//
// **This screen writes no audit row of its own, and adding one would be a
// change rather than a fix.** The trail belongs to no customer, so reading it
// crosses no boundary and there is no scope to pass — and a row written every
// time somebody opened the log would fill the log with lookings rather than
// readings, and would make it grow by being read. The asymmetry is deliberate
// on the backend and is ported as it stands.
//
// The window is the recent stretch, not the whole table: the trail is
// append-only and grows without bound, and anything older than the window is a
// query against the database rather than a page.

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { when } from '../data/instants';
import { readSeeAllAudit, type SeeAllAuditScreen } from '../data/acrossCustomers';
import { usePortalRead } from '../data/usePortalRead';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'See-All Audit';

export function SeeAllAuditPage() {
  useDocumentTitle(TITLE);
  const screen = usePortalRead(readSeeAllAudit);

  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }
  if (screen.status === 'failed') {
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }
  return <SeeAllAudit screen={screen.data} />;
}

function SeeAllAudit({ screen }: { screen: SeeAllAuditScreen }) {
  const { accesses } = screen;

  return (
    <div className="space-y-5 p-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          metricsIQ · Across every customer
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{TITLE}</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Every read that crossed customer boundaries, newest first. Rows are
          written before the data is returned and are never updated.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent reads</CardTitle>
          <CardDescription>
            {accesses.length === 1 ? '1 read' : `${accesses.length} reads`}.
            Opening this screen is not itself one of them.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {accesses.length === 0 ? (
            // An empty trail is the true answer, not a failure: nobody has
            // read across customers yet, which is what a service in its first
            // week looks like.
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No cross-customer reads have happened.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Who</TableHead>
                    <TableHead>What</TableHead>
                    <TableHead>Why</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accesses.map((access, index) => (
                    // Keyed on position: the trail carries no identifier for a
                    // row, and several rows share a moment — one screen-open
                    // writes five of them inside one request. The window is
                    // read once and never reorders under the reader.
                    <TableRow key={index}>
                      <TableCell className="whitespace-nowrap">
                        {when(access.occurred_at)}
                      </TableCell>
                      <TableCell className="font-medium">{access.actor}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {access.model_label}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {access.reason}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
